import React, { useState, useEffect } from "react";

function formatEventTime(event) {
  if (event.isAllDay) return "All day";
  const d = new Date(event.start);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function formatEventDay(event) {
  const d = new Date(event.isAllDay ? event.start + "T00:00:00" : event.start);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

const CALENDAR_COLORS = {
  family: "#7A9E7E",
  adam: "#5A8A6A",
  kimberly: "#C9847A",
  personal: "#D4A847",
  work: "#6B8EAD",
  photography: "#8A7ACC",
  rsp3studio: "#5B8DB8",
};

const CALENDAR_LABELS = {
  family: "Family",
  adam: "Adam",
  kimberly: "Kimberly",
  personal: "Personal",
  work: "Work",
  photography: "Photography",
  rsp3studio: "RSP3",
};

const GCal_URL = "https://calendar.google.com/calendar/r";

export default function CalendarSection({ view = "main" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/.netlify/functions/calendar?view=${view}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setError(d.error); setEvents([]); }
        else setEvents(d.events || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [view]);

  const groups = [];
  const seen = new Map();
  for (const event of events) {
    const dayLabel = formatEventDay(event);
    if (!seen.has(dayLabel)) {
      seen.set(dayLabel, []);
      groups.push({ label: dayLabel, events: seen.get(dayLabel) });
    }
    seen.get(dayLabel).push(event);
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px 6px" }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)" }}>📅 Calendar</span>
        <a href={GCal_URL} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 700, color: "var(--sage)", textDecoration: "none" }}>Open ↗</a>
      </div>
      <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)" }}>
        {loading && <div style={{ padding: "16px 20px", color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>Loading…</div>}
        {error && <div style={{ padding: "12px 20px", color: "var(--rose)", fontSize: 13 }}>Could not load calendar</div>}
        {!loading && !error && events.length === 0 && (
          <div style={{ padding: "20px", color: "var(--muted)", fontSize: 14, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>🎉</div>Nothing coming up
          </div>
        )}
        {!loading && !error && groups.map(({ label, events: dayEvents }) => (
          <div key={label}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", padding: "10px 20px 4px", background: "var(--cream-hover)", borderBottom: "1px solid var(--border)", borderTop: "1px solid var(--border)" }}>{label}</div>
            {dayEvents.map(event => (
              <a key={event.id} href={event.htmlLink || GCal_URL} target="_blank" rel="noreferrer"
                style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 20px", borderBottom: "1px solid var(--border)", textDecoration: "none", background: "var(--white)" }}>
                <div style={{ width: 3, borderRadius: 2, alignSelf: "stretch", background: CALENDAR_COLORS[event.calendar] || "var(--sage)", flexShrink: 0, minHeight: 20, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--charcoal)", lineHeight: 1.3 }}>{event.title}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>{formatEventTime(event)}</span>
                    <span style={{ fontSize: 11, color: CALENDAR_COLORS[event.calendar] || "var(--sage)", fontWeight: 700, padding: "1px 7px", borderRadius: 20, background: `${CALENDAR_COLORS[event.calendar]}22` }}>
                      {CALENDAR_LABELS[event.calendar] || event.calendar}
                    </span>
                  </div>
                  {event.location && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>📍 {event.location}</div>}
                </div>
                <span style={{ fontSize: 16, color: "var(--muted)", flexShrink: 0, marginTop: 2 }}>›</span>
              </a>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
